/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid, Tooltip } from '@mairistem/composants';
import { ParentForm, TypeComponent } from '@mairistem/administres-utils';

import { GridColumnDivider, Section } from '@/components/Common';
// import ParentForm from '@/components/Common/ParentForm';
import { AssistantContext } from '@/contexts';
import { Steps } from '@/enums';
import { hasRepresentantsLegaux, sexeToSexeCode } from '@/utils';

interface StepParentsPartenaireProps {
  numeroPartenaire: number;
}

const StepParentsPartenaire = (props: StepParentsPartenaireProps) => {
  const { numeroPartenaire } = props;

  const {
    conventionDeclaration,
    filiationInconnueMerePartenaire1,
    filiationInconnuePerePartenaire1,
    filiationInconnueMerePartenaire2,
    filiationInconnuePerePartenaire2,
    errors,
    onChange,
  } = React.useContext(AssistantContext);

  const isPartenaire1 = numeroPartenaire === 1;

  const testIdPrefix = `assistant-partenaire-${numeroPartenaire}-parents`;

  // Y compris en mode Hubee : les protections juridique/OFPRA préremplies
  // depuis la pré-demande font des parents les représentants légaux.
  const representantsLegaux = hasRepresentantsLegaux(
    conventionDeclaration?.[`partenaire${numeroPartenaire}`],
  );

  const parent1 = isPartenaire1
    ? conventionDeclaration?.perePartenaire1
    : conventionDeclaration?.perePartenaire2;
  const parent2 = isPartenaire1
    ? conventionDeclaration?.merePartenaire1
    : conventionDeclaration?.merePartenaire2;

  // Sous protection juridique, au moins un représentant légal doit être
  // identifié
  const representantLegalObligatoire =
    conventionDeclaration?.[`partenaire${numeroPartenaire}`]
      ?.protectionJuridique === true;

  const filiationInconnueParent1 = isPartenaire1
    ? filiationInconnuePerePartenaire1
    : filiationInconnuePerePartenaire2;
  const filiationInconnueParent2 = isPartenaire1
    ? filiationInconnueMerePartenaire1
    : filiationInconnueMerePartenaire2;

  const onChangeParent = React.useCallback(
    (_e, data) => {
      if (!data?.name) {
        onChange(_e, data);
        return;
      }

      const suffix = isPartenaire1 ? '1' : '2';
      let mappedName = data.name;

      if (data.name === 'filiationInconnueParent1') {
        mappedName = `filiationInconnuePerePartenaire${suffix}`;
      } else if (data.name === 'filiationInconnueParent2') {
        mappedName = `filiationInconnueMerePartenaire${suffix}`;
      } else if (data.name.startsWith('parent1.')) {
        mappedName = data.name.replace(
          'parent1.',
          `conventionDeclaration.perePartenaire${suffix}.`,
        );
      } else if (data.name.startsWith('parent2.')) {
        mappedName = data.name.replace(
          'parent2.',
          `conventionDeclaration.merePartenaire${suffix}.`,
        );
      }

      onChange(_e, {
        ...data,
        name: mappedName,
      });

      // EtatCivil ne gérant que .sexe, on re-calcul sexeCode pour
      // rester synchroniser
      if (data.name === 'parent1.sexe' || data.name === 'parent2.sexe') {
        onChange(_e, {
          name: mappedName.replace(/\.sexe$/, '.sexeCode'),
          value: sexeToSexeCode(data.value),
        });
      }

      // Si la commune déléguée est décochée, on vide le libellé associé
      // pour ne pas laisser une saisie orpheline non affichable.
      if (
        data.name === 'parent1.adresseNaissance.naissanceCommuneDelegue' ||
        data.name === 'parent2.adresseNaissance.naissanceCommuneDelegue'
      ) {
        if (!data.checked) {
          onChange(_e, {
            name: mappedName.replace(
              'naissanceCommuneDelegue',
              'naissanceLibelleCommuneDelegue',
            ),
            value: '',
          });
        }
      }
    },
    [isPartenaire1, onChange],
  );

  const [step, setStep] = React.useState<number>(Steps.ParentsPartenaire1);

  React.useEffect(() => {
    if (numeroPartenaire === 1) {
      setStep(Steps.ParentsPartenaire1);
    } else {
      setStep(Steps.ParentsPartenaire2);
    }
  }, [numeroPartenaire]);

  const errorsStep = errors?.[step] || {};

  return (
    <Grid as={Form} padded>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Grid.Row style={{ paddingTop: 0 }}>
          <Grid.Column>
            <Section
              data-testid={testIdPrefix}
              title={`INFORMATIONS DES ${
                representantsLegaux ? 'REPRÉSENTANTS LÉGAUX' : 'PARENTS'
              } DU PARTENAIRE ${numeroPartenaire}`}
            >
              <Grid centered>
                <Grid.Column computer={7} mobile={16}>
                  <ParentForm
                    title={
                      representantsLegaux ? 'Représentant légal 1' : 'Parent 1'
                    }
                    numeroParent={1}
                    parent={parent1}
                    filiationInconnue={filiationInconnueParent1}
                    identiteObligatoire={representantLegalObligatoire}
                    errors={errorsStep?.parent1}
                    onChange={onChangeParent}
                    typeComponent={TypeComponent.PACS}
                  />
                  <Grid padded>
                    <Grid.Row verticalAlign="middle" style={{ padding: 0 }}>
                      <Grid.Column width={6}>
                        <label>&nbsp;</label>
                        <Form.Checkbox
                          name="parent1.adresseNaissance.naissanceCommuneDelegue"
                          label="Commune déléguée"
                          data-testid={`${testIdPrefix}-parent-1-commune-delegue`}
                          checked={
                            parent1?.adresseNaissance?.naissanceCommuneDelegue
                          }
                          onChange={onChangeParent}
                        />
                      </Grid.Column>
                      <Grid.Column width={10}>
                        <Tooltip
                          trigger={
                            <div>
                              <Form.Input
                                name="parent1.adresseNaissance.naissanceLibelleCommuneDelegue"
                                label="&nbsp;"
                                input={{
                                  'data-testid': `${testIdPrefix}-parent-1-libelle-commune-delegue`,
                                }}
                                value={
                                  parent1?.adresseNaissance
                                    ?.naissanceLibelleCommuneDelegue ?? ''
                                }
                                disabled={
                                  !parent1?.adresseNaissance
                                    ?.naissanceCommuneDelegue
                                }
                                error={
                                  !!errorsStep?.parent1
                                    ?.naissanceLibelleCommuneDelegue
                                }
                                onChange={onChangeParent}
                              />
                            </div>
                          }
                          type={
                            errorsStep?.parent1?.naissanceLibelleCommuneDelegue
                              ? 'error'
                              : ''
                          }
                          message={
                            errorsStep?.parent1?.naissanceLibelleCommuneDelegue
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
                <GridColumnDivider />
                <Grid.Column style={{ marginLeft: 0 }} computer={7} mobile={16}>
                  <ParentForm
                    title={
                      representantsLegaux ? 'Représentant légal 2' : 'Parent 2'
                    }
                    numeroParent={2}
                    parent={parent2}
                    filiationInconnue={filiationInconnueParent2}
                    errors={errorsStep?.parent2}
                    onChange={onChangeParent}
                    typeComponent={TypeComponent.PACS}
                  />
                  <Grid padded>
                    <Grid.Row verticalAlign="middle" style={{ padding: 0 }}>
                      <Grid.Column width={6}>
                        <label>&nbsp;</label>
                        <Form.Checkbox
                          name="parent2.adresseNaissance.naissanceCommuneDelegue"
                          label="Commune déléguée"
                          data-testid={`${testIdPrefix}-parent-2-commune-delegue`}
                          checked={
                            parent2?.adresseNaissance?.naissanceCommuneDelegue
                          }
                          onChange={onChangeParent}
                        />
                      </Grid.Column>
                      <Grid.Column width={10}>
                        <Tooltip
                          trigger={
                            <div>
                              <Form.Input
                                name="parent2.adresseNaissance.naissanceLibelleCommuneDelegue"
                                label="&nbsp;"
                                input={{
                                  'data-testid': `${testIdPrefix}-parent-2-libelle-commune-delegue`,
                                }}
                                value={
                                  parent2?.adresseNaissance
                                    ?.naissanceLibelleCommuneDelegue ?? ''
                                }
                                disabled={
                                  !parent2?.adresseNaissance
                                    ?.naissanceCommuneDelegue
                                }
                                error={
                                  !!errorsStep?.parent2
                                    ?.naissanceLibelleCommuneDelegue
                                }
                                onChange={onChangeParent}
                              />
                            </div>
                          }
                          type={
                            errorsStep?.parent2?.naissanceLibelleCommuneDelegue
                              ? 'error'
                              : ''
                          }
                          message={
                            errorsStep?.parent2?.naissanceLibelleCommuneDelegue
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid>
            </Section>
          </Grid.Column>
        </Grid.Row>
      </div>
    </Grid>
  );
};

export default StepParentsPartenaire;
